import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, TextField, Button, Grid, InputAdornment, Tooltip, CircularProgress, Checkbox, FormControlLabel } from '@mui/material';
import { toast } from 'react-toastify';

const GiveQuoteModal = ({ open, onClose, rowData, selectedAgency, refreshHandler }) => {
  const [executionFee, setExecutionFee] = useState('0'); // Initialize to '0' since it's fetched
  const [travelFee, setTravelFee] = useState('0'); // Default to 0
  const [miscellaneousFees, setMiscellaneousFees] = useState('0'); // Default to 0
  const [clarificationText, setClarificationText] = useState('');
  const [promisedPostingTime, setPromisedPostingTime] = useState(''); // In hours
  const [quotesValidDuration, setQuotesValidDuration] = useState(''); // Default to 3 hours, with a minimum of 3
  const [postingStipulations, setPostingStipulations] = useState(''); // Posting stipulations
  const [paymentRequiredUpFront, setPaymentRequiredUpFront] = useState(false); // Payment required up front
  const [loading, setLoading] = useState(false); // Loading state for preventing duplicate submits
  const [isFetching, setIsFetching] = useState(false); // Loading state for fetching default fees

  // Fetch default fees when modal opens
  useEffect(() => {
    if (open && rowData && selectedAgency) {
      fetchDefaultFees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rowData, selectedAgency]);

  const fetchDefaultFees = async () => {
    setIsFetching(true);
    try {
      const token = localStorage.getItem('jwtToken');
      const baseURL = process.env.REACT_APP_BASE_URL;

      const response = await fetch(
        `${baseURL}PostingTransferBond/${selectedAgency.id}/DefaultQuotes/${rowData.transferBondTransactionID}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch default fees');
      }

      const data = await response.json();

      // Prepopulate the form fields with data from the response or default to 0
      setExecutionFee(data.tbondExecutionFeeQuote ? data.tbondExecutionFeeQuote.toString() : '0');
      setTravelFee(data.tbondTravelQuote ? data.tbondTravelQuote.toString() : '0');
      setMiscellaneousFees(data.tbondMisQuote ? data.tbondMisQuote.toString() : '0');
      setQuotesValidDuration(data.quoteDuration ? (data.quoteDuration / 60).toString() : '0');
      setPromisedPostingTime(data.timeToPost ? (data.timeToPost / 60).toString() : '0');

    } catch (error) {
      toast.error('Error fetching default fees.');
    } finally {
      setIsFetching(false);
    }
  };

  const handleCurrencyChange = (setter) => (event) => {
    const value = event.target.value.replace(/[^0-9.]/g, ''); // Ensure input is only numbers and decimal
    setter(value);
  };

  const handleTimeChange = (setter) => (event) => {
    const value = event.target.value.replace(/[^0-9]/g, ''); // Ensure input is only numbers
    setter(value);
  };

  const clearFields = () => {
    setExecutionFee('0');
    setTravelFee('0');
    setMiscellaneousFees('0');
    setClarificationText('');
    setPromisedPostingTime('');
    setQuotesValidDuration('');
    setPostingStipulations('');
    setPaymentRequiredUpFront(false);
  };

  const handleSaveQuote = async () => {
    if (!travelFee || !promisedPostingTime || !quotesValidDuration) { // Removed executionFee from validation
      toast.error('Please fill out all required fields.');
      return;
    }
    if (promisedPostingTime < 1 || quotesValidDuration < 1) {
      toast.error('Promised posting time must be at least 1 hour and quote duration must be at least 1 hour.');
      return;
    }

    setLoading(true); // Start loading
    const promisedPostingTimeInMinutes = parseInt(promisedPostingTime) * 60;
    const quotesValidDurationInMinutes = parseInt(quotesValidDuration) * 60;

    const quoteData = {
      transferBondTransactionID: rowData?.transferBondTransactionID, // Passed from the parent
      executionFee: parseFloat(executionFee), // Use fetched executionFee
      travelFee: parseFloat(travelFee),
      otherFee: parseFloat(miscellaneousFees),
      feeDescription: paymentRequiredUpFront
        ? (clarificationText ? `${clarificationText}; State taxes` : 'State taxes')
        : clarificationText,
      promisedPostingTime: promisedPostingTimeInMinutes,
      quotesValidDuration: quotesValidDurationInMinutes,
      agentID: selectedAgency?.id, // Pass AgentID from selectedAgency
      postingStipulations: postingStipulations,
      paymentRequiredUpFront: paymentRequiredUpFront,
    };

    try {
      const token = localStorage.getItem('jwtToken'); // Assuming token is stored in local storage
      const baseURL = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${baseURL}PostingTransferBond/${rowData?.transferBondTransactionID}/Quote`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(quoteData),
      });

      if (!response.ok) {
        throw new Error(`Error submitting quote: ${response.statusText}`);
      }

      toast.success('Quote submitted successfully.');
      clearFields();
      refreshHandler(); // Refresh the parent component
      onClose();
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Error submitting the quote.');
    } finally {
      setLoading(false); // Stop loading
    }
  };

  const handleClose = () => {
    clearFields(); // Clear fields when closing
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="give-quote-modal"
      aria-describedby="give-quote-form"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: '70%', md: '50%', lg: '40%' }, // Adjusted width for smaller modal
          maxHeight: '85vh', // Reduced maximum height for scrollable content
          overflowY: 'auto', // Enable scrolling
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2, // Slightly rounded corners
        }}
      >
        {isFetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Centered title */}
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
              Provide a Quote ({rowData?.transferBondTransactionID})
            </Typography>

            {/* Jail/Courthouse - Takes up 4 columns, Other fields take up remaining 8 */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      <strong>Jail:</strong> {rowData?.jailLocation}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      <strong>State:</strong> {rowData?.state}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      <strong>Number of Bonds:</strong> {rowData?.numberOfBonds}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>Courthouse:</strong> {rowData?.courtPlace}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <strong>County:</strong> {rowData?.county}
                </Typography>
              </Grid>
            </Grid>

            {/* First Section: Fees */}
            <Typography variant="h6" sx={{ mb: 2 }}>Fees</Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Execution fee is standard and doesn't change, travel is reasonable travel expenses, misc is jail fees and/or any additional expense
            </Typography>
            <Grid container spacing={2}>
              {/* Execution Fee - Displayed as a disabled TextField */}
              <Grid item xs={12} md={4}>
                <TextField
                  label="Execution Fee"
                  value={`${parseFloat(executionFee).toFixed(2)}`}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    readOnly: true, // Make the field read-only
                  }}
                  variant="outlined"
                  disabled // Alternatively, you can use the disabled prop
                />
              </Grid>
              
              {/* Travel Fee */}
              <Grid item xs={12} md={4}>
                <TextField
                  label="Travel Fee"
                  value={travelFee}
                  onChange={handleCurrencyChange(setTravelFee)}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  required
                />
              </Grid>
              
              {/* Miscellaneous Fees */}
              <Grid item xs={12} md={4}>
                <TextField
                  label="Miscellaneous Fees"
                  value={miscellaneousFees}
                  onChange={handleCurrencyChange(setMiscellaneousFees)}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>

              {/* Description of Misc Fees */}
              <Grid item xs={12}>
                <TextField
                  label="Description Of Misc Fees"
                  value={clarificationText}
                  onChange={(e) => setClarificationText(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>

            {/* Second Section: Posting Details */}
            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Posting Details</Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              The posting details represent how long it takes you to post this bond and how long the quote is valid for.
            </Typography>
            <Grid container spacing={2}>
              {/* Promised Posting Time and Quoted Valid Duration with tooltips */}
              <Grid item xs={12} md={6}>
                <Tooltip title="How long it will take you to post this bond" arrow>
                  <TextField
                    label="Promised Posting Time (hours)"
                    value={promisedPostingTime}
                    onChange={handleTimeChange(setPromisedPostingTime)}
                    fullWidth
                    required
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={6}>
                <Tooltip title="How long is this quote good for?" arrow>
                  <TextField
                    label="Quoted Valid Duration (hours)"
                    value={quotesValidDuration}
                    onChange={handleTimeChange(setQuotesValidDuration)}
                    fullWidth
                    required
                  />
                </Tooltip>
              </Grid>

              {/* Posting Stipulations */}
              <Grid item xs={12}>
                <TextField
                  label="Posting Stipulations"
                  value={postingStipulations}
                  onChange={(e) => setPostingStipulations(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                />
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                  Posting stipulations can be considered as a message to the requesting agent.
                </Typography>
              </Grid>

              {/* Payment Required Up Front - Only visible when state is Lousiana */}
              {rowData?.state === 'LA' && (
                <Grid item xs={12}>
                  <Tooltip title="Require the requesting agent to pay up front before posting the bond" arrow>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={paymentRequiredUpFront}
                          onChange={(e) => setPaymentRequiredUpFront(e.target.checked)}
                        />
                      }
                      label="Payment Required Up Front"
                    />
                  </Tooltip>
                </Grid>
              )}
            </Grid>

            {/* Action Buttons */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveQuote}
                disabled={loading} // Disable the button while loading
                startIcon={loading ? <CircularProgress size={20} /> : null} // Add a spinner
              >
                Submit Quote
              </Button>
              <Button variant="outlined" color="secondary" onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default GiveQuoteModal;
